import _ from 'lodash';

import { BorrowerTypeLabel, LoanApplicationTabName } from 'constants/loanApplication';

import { SearchCategory } from '../UniversalSearch.constants';

import { resolveFieldValue } from './fields.utils';
import { FinancialInfoViewName } from './navigation.constants';
import {
  RECORD_FIELD_DEFINITIONS,
  RECORD_TYPE_CONFIGS,
  RECORD_TYPE_LABEL_FNS,
} from './records.config';
import { FINANCIAL_INFO_TAB_NAME, FINANCIAL_INFO_TAB_ROUTE } from './records.constants';

export { getAssetLabel, getIncomeLabel, getLiabilityLabel } from './records.labels';

const getOwnerNames = record => {
  if (record.ownersInfo?.length) {
    return record.ownersInfo.map(owner => owner.fullName).join(', ');
  }

  if (record.borrower?.fullName) {
    return record.borrower.fullName;
  }

  return '';
};

const buildRecordFieldItems = ({ record, recordName, tablePath, tabRoute, ownerNames }) => {
  const fieldDefinitions = RECORD_FIELD_DEFINITIONS[tablePath];

  if (!fieldDefinitions) {
    return [];
  }

  const typeLabel = FinancialInfoViewName[tablePath];
  const ownerSuffix = ownerNames ? ` > ${ownerNames}` : '';

  return fieldDefinitions.map(({ fieldId, label, dataPath }) => {
    const fieldValue = resolveFieldValue({ dataSource: record, dataPath });

    return {
      id: `record-field-${tablePath}-${record.id}-${fieldId}`,
      label: `${recordName} > ${label}`,
      category: SearchCategory.RECORDS,
      breadcrumb: `${FINANCIAL_INFO_TAB_NAME} > ${typeLabel}${ownerSuffix}`,
      recordType: tablePath,
      recordId: record.id,
      tablePath,
      tabRoute,
      fieldId,
      isRecordField: true,
      ...(fieldValue ? { fieldValue } : {}),
    };
  });
};

const buildTypedRecordItems = ({ records, tablePath, getNameFn, getLabelFn }) =>
  records.flatMap(record => {
    const recordName = getNameFn(record);
    const recordLabel = getLabelFn(record);
    const ownerNames = getOwnerNames(record);
    const ownerSuffix = ownerNames ? ` > ${ownerNames}` : '';

    const baseItem = {
      id: `record-${tablePath}-${record.id}`,
      label: recordLabel,
      category: SearchCategory.RECORDS,
      breadcrumb: `${FINANCIAL_INFO_TAB_NAME} > ${FinancialInfoViewName[tablePath]}${ownerSuffix}`,
      recordType: tablePath,
      recordId: record.id,
      tablePath,
      tabRoute: FINANCIAL_INFO_TAB_ROUTE,
    };

    const fieldItems = buildRecordFieldItems({
      record,
      recordName,
      tablePath,
      tabRoute: FINANCIAL_INFO_TAB_ROUTE,
      ownerNames,
    });

    return [baseItem, ...fieldItems];
  });

export const buildRecordsIndex = ({
  borrowers = [],
  liabilities = [],
  incomes = [],
  assets = [],
  properties = [],
}) => {
  const dataMap = { liabilities, incomes, assets, properties };

  const borrowerItems = borrowers.map(borrower => ({
    id: `record-borrower-${borrower.borrowerId}`,
    label: `${borrower.fullName} (${BorrowerTypeLabel[borrower.borrowerType] ?? 'Borrower'})`,
    category: SearchCategory.RECORDS,
    breadcrumb: 'Borrowers',
    recordType: 'borrower',
    recordId: borrower.borrowerId,
    tabRoute: _.kebabCase(LoanApplicationTabName.BORROWERS),
  }));

  const financialItems = RECORD_TYPE_CONFIGS.flatMap(({ dataKey, tablePath }) => {
    const { getNameFn, getLabelFn } = RECORD_TYPE_LABEL_FNS[tablePath];

    return buildTypedRecordItems({
      records: dataMap[dataKey] || [],
      tablePath,
      getNameFn,
      getLabelFn,
    });
  });

  return [...borrowerItems, ...financialItems];
};
